import { useEffect, useRef, useState } from 'react';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { Wand2, Loader2, Crown, X, CheckCircle2, AlertTriangle, History, Play, Pause, Music } from 'lucide-react';

const TAB_ANALYZE = 'analyze';
const TAB_HISTORY = 'history';

export default function AudioAnalyzeButton({ audioUrl, songId, timeSignature, isPro, onApply }) {
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState(TAB_ANALYZE);
  const [quota, setQuota] = useState(null);
  const [job, setJob] = useState(null);
  const [history, setHistory] = useState([]);
  const [error, setError] = useState('');
  const pollRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    api.get('/audio/analyze/quota').then(r => setQuota(r.data)).catch(() => {});
    api.get('/audio/analyses/history').then(r => setHistory(r.data)).catch(() => {});
  }, [open]);

  useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);

  const start = async () => {
    setError('');
    setJob(null);
    try {
      const { data } = await api.post('/audio/analyze', {
        audio_url: audioUrl,
        song_id: songId,
        time_signature: timeSignature || '4/4',
      });
      setJob({ id: data.job_id, status: data.status, progress: 0 });
      setQuota(q => q ? { ...q, used: (q.used || 0) + 1 } : q);
      pollRef.current = setInterval(async () => {
        try {
          const r = await api.get(`/audio/analyze/${data.job_id}`);
          setJob(r.data);
          if (r.data.status === 'done' || r.data.status === 'failed') {
            clearInterval(pollRef.current);
            pollRef.current = null;
            if (r.data.status === 'failed') setError(r.data.error || 'Analyse échouée');
            if (r.data.status === 'done') {
              // Refresh history so this run appears in the list
              api.get('/audio/analyses/history').then(res => setHistory(res.data)).catch(() => {});
            }
          }
        } catch (e) { /* keep polling */ }
      }, 1500);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail?.error === 'quota_exceeded') setError(detail.message);
      else setError(e?.response?.data?.detail?.message || e?.response?.data?.detail || 'Impossible de lancer l\'analyse');
    }
  };

  const apply = (result) => {
    onApply({
      chord_timeline: result.chord_timeline,
      bpm: result.bpm,
      time_signature: result.time_signature,
    });
    const sections = result.sections?.length ? ` · ${result.sections.length} section${result.sections.length > 1 ? 's' : ''}` : '';
    toast.success(`Timeline générée : ${result.chord_count} accord(s), ${result.bpm} BPM${sections}`);
    setOpen(false);
    setJob(null);
  };

  const reuse = async (jobId) => {
    // Fetch full result and apply — no quota consumed
    try {
      const r = await api.get(`/audio/analyze/${jobId}`);
      if (r.data.status === 'done' && r.data.result) {
        apply(r.data.result);
        toast.info('Analyse réappliquée (sans consommer de quota)');
      }
    } catch { toast.error('Impossible de récupérer cette analyse'); }
  };

  const remaining = quota?.is_pro ? null : Math.max(0, (quota?.limit || 3) - (quota?.used || 0));
  const disabled = !audioUrl;

  return (
    <>
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen(true)}
        data-testid="studio-analyze-open"
        title={disabled ? "Téléverse d'abord un fichier audio" : "Détecter automatiquement les accords, le BPM et les mesures"}
        className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${
          disabled
            ? 'bg-stone-800 text-stone-500 cursor-not-allowed'
            : 'bg-gradient-to-r from-orange-700 to-amber-700 hover:from-orange-600 hover:to-amber-600 text-white shadow-md shadow-orange-950/40'
        }`}
      >
        <Wand2 size={13} />
        Détecter automatiquement
      </button>

      {open && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" role="dialog" data-testid="studio-analyze-modal">
          <div className="w-full max-w-2xl max-h-[90vh] overflow-y-auto bg-stone-950 border border-stone-800 rounded-2xl p-6 rise">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <div className="w-9 h-9 rounded-lg bg-orange-700/20 border border-orange-800/60 flex items-center justify-center">
                  <Wand2 size={16} className="text-orange-400" />
                </div>
                <div>
                  <div className="font-display text-xl leading-none">Détection automatique</div>
                  <div className="text-xs text-stone-500 mt-0.5">Accords · BPM · Mesures · Sections</div>
                </div>
              </div>
              <button onClick={() => { setOpen(false); setJob(null); }} className="text-stone-500 hover:text-stone-300"><X size={18} /></button>
            </div>

            {/* Tabs */}
            <div className="flex border-b border-stone-800 mb-4">
              <button onClick={() => setTab(TAB_ANALYZE)} data-testid="analyze-tab-run" className={`px-3 py-2 text-xs border-b-2 ${tab === TAB_ANALYZE ? 'border-orange-500 text-stone-100' : 'border-transparent text-stone-500'}`}>Nouvelle analyse</button>
              <button onClick={() => setTab(TAB_HISTORY)} data-testid="analyze-tab-history" className={`px-3 py-2 text-xs border-b-2 flex items-center gap-1 ${tab === TAB_HISTORY ? 'border-orange-500 text-stone-100' : 'border-transparent text-stone-500'}`}>
                <History size={11} /> Historique{history.length ? ` (${history.length})` : ''}
              </button>
            </div>

            {tab === TAB_ANALYZE && (
              <>
                {quota && !quota.is_pro && (
                  <div className="mb-4 rounded-lg border border-stone-800 bg-stone-900/60 p-3 flex items-center gap-3">
                    <div className="text-2xl">{remaining > 0 ? '✨' : '⏳'}</div>
                    <div className="flex-1">
                      <div className="text-sm text-stone-100 font-medium">{remaining} / {quota.limit} analyses restantes ce mois</div>
                      <div className="text-xs text-stone-500">Passe Pro pour un usage illimité</div>
                    </div>
                    {remaining === 0 && <a href="/pro" className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-orange-700 hover:bg-orange-600 text-white text-xs"><Crown size={12} /> Pro</a>}
                  </div>
                )}
                {quota?.is_pro && (
                  <div className="mb-4 rounded-lg border border-emerald-800/60 bg-emerald-950/30 p-3 flex items-center gap-2 text-emerald-300 text-sm">
                    <Crown size={14} /> Analyses illimitées (Pro)
                  </div>
                )}

                {job && job.status !== 'done' && job.status !== 'failed' && (
                  <div className="rounded-lg border border-stone-800 bg-stone-900 p-4">
                    <div className="flex items-center gap-2 text-stone-100 text-sm mb-2">
                      <Loader2 size={14} className="animate-spin text-orange-400" />
                      {job.status === 'queued' ? 'En attente…' : job.progress < 40 ? 'Téléchargement de l\'audio…' : job.progress < 90 ? 'Analyse en cours…' : 'Presque fini…'}
                    </div>
                    <div className="h-2 rounded-full bg-stone-800 overflow-hidden">
                      <div className="h-full bg-gradient-to-r from-orange-600 to-amber-500 transition-all" style={{ width: `${job.progress || 5}%` }} data-testid="studio-analyze-progress" />
                    </div>
                  </div>
                )}

                {job?.status === 'done' && job.result && (
                  <ResultPreview result={job.result} audioUrl={audioUrl} onApply={() => apply(job.result)} onRestart={() => { setJob(null); setError(''); }} />
                )}

                {error && (
                  <div className="mt-3 rounded-lg border border-red-900/60 bg-red-950/30 p-3 flex items-start gap-2 text-red-300 text-sm" data-testid="studio-analyze-error">
                    <AlertTriangle size={14} className="mt-0.5 shrink-0" />
                    <div>{error}</div>
                  </div>
                )}

                {!job && (
                  <div className="mt-5 flex justify-end">
                    <button onClick={start} disabled={quota && !quota.is_pro && remaining === 0} data-testid="studio-analyze-start" className="btn-primary text-sm disabled:opacity-40 disabled:cursor-not-allowed">
                      <Wand2 size={13} /> Lancer l'analyse
                    </button>
                  </div>
                )}
              </>
            )}

            {tab === TAB_HISTORY && (
              <HistoryPanel history={history} onReuse={reuse} />
            )}
          </div>
        </div>
      )}
    </>
  );
}

// ---------- RESULT PREVIEW with synced audio playback ----------
function ResultPreview({ result, audioUrl, onApply, onRestart }) {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [currentIdx, setCurrentIdx] = useState(-1);
  const [currentSection, setCurrentSection] = useState('');
  const rafRef = useRef(null);
  const secondsPerBeat = 60 / (result.bpm || 90);

  // Build cumulative time for each timeline row using chords_seconds when available,
  // else derive from beats × seconds_per_beat.
  const rowStarts = (() => {
    const seconds = result.chords_seconds || [];
    if (seconds.length && seconds.length === result.chord_timeline.length) {
      return seconds.map(s => s.start);
    }
    const out = [];
    let acc = 0;
    for (const row of result.chord_timeline) {
      out.push(acc);
      acc += row.beats * secondsPerBeat;
    }
    return out;
  })();

  useEffect(() => {
    if (!playing || !audioRef.current) return;
    const tick = () => {
      const t = audioRef.current?.currentTime || 0;
      let idx = -1;
      for (let i = 0; i < rowStarts.length; i++) {
        if (rowStarts[i] <= t) idx = i;
        else break;
      }
      setCurrentIdx(idx);
      const sec = result.sections?.find(s => s.start <= t && t < s.end);
      setCurrentSection(sec?.label || '');
      rafRef.current = requestAnimationFrame(tick);
    };
    tick();
    return () => cancelAnimationFrame(rafRef.current);
  }, [playing, rowStarts, result.sections]);

  const toggle = () => {
    if (!audioRef.current) return;
    if (playing) { audioRef.current.pause(); setPlaying(false); }
    else { audioRef.current.play().catch(() => {}); setPlaying(true); }
  };

  return (
    <div className="rounded-lg border border-emerald-800/60 bg-emerald-950/20 p-4 space-y-3" data-testid="studio-analyze-result">
      <div className="flex items-center gap-2 text-emerald-300 text-sm">
        <CheckCircle2 size={14} /> Analyse terminée
      </div>
      <div className="grid grid-cols-4 gap-2 text-center">
        <StatMini label="BPM" value={result.bpm} />
        <StatMini label="Mesures" value={result.chord_count} />
        <StatMini label="Uniques" value={result.distinct_chords} />
        <StatMini label="Sections" value={result.sections?.length || 0} />
      </div>

      {/* Sections chips */}
      {result.sections?.length > 0 && (
        <div className="flex flex-wrap gap-1.5" data-testid="analyze-sections">
          {result.sections.map((s, i) => (
            <span key={i} className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${currentSection === s.label ? 'bg-orange-600 text-white' : 'bg-stone-800 text-stone-300 border border-stone-700'}`}>
              {s.label}
            </span>
          ))}
        </div>
      )}

      {/* Audio preview + synced pills */}
      {audioUrl && (
        <div className="rounded-lg bg-stone-950/70 border border-stone-800 p-2 space-y-2">
          <div className="flex items-center gap-2">
            <button type="button" onClick={toggle} data-testid="analyze-preview-toggle" className="w-9 h-9 rounded-full bg-orange-700 hover:bg-orange-600 text-white flex items-center justify-center shrink-0">
              {playing ? <Pause size={14} /> : <Play size={14} />}
            </button>
            <div className="text-[11px] text-stone-400 flex-1">
              {playing ? (
                <>♪ Lecture — {currentSection && <span className="text-orange-300">{currentSection} · </span>}
                {currentIdx >= 0 && <span className="text-stone-200 font-mono">{result.chord_timeline[currentIdx]?.chord}</span>}</>
              ) : (
                'Écoute l\'audio original pour vérifier les accords détectés'
              )}
            </div>
            <audio ref={audioRef} src={audioUrl} onEnded={() => { setPlaying(false); setCurrentIdx(-1); }} className="hidden" preload="metadata" />
          </div>
          <div className="flex flex-wrap gap-1 max-h-32 overflow-y-auto">
            {result.chord_timeline.slice(0, 64).map((row, i) => (
              <span
                key={i}
                onClick={() => {
                  if (audioRef.current && rowStarts[i] !== undefined) {
                    audioRef.current.currentTime = rowStarts[i];
                    if (!playing) { audioRef.current.play().catch(() => {}); setPlaying(true); }
                  }
                }}
                className={`text-xs px-2 py-0.5 rounded-full cursor-pointer border transition-colors ${
                  i === currentIdx
                    ? 'bg-orange-600 text-white border-orange-500 scale-110'
                    : 'bg-stone-800 text-stone-200 border-stone-700 hover:border-orange-800'
                }`}
                title={row.target_note ? `Note-cible chant : ${row.target_note}` : undefined}
              >
                {row.chord} <span className="opacity-60">· {row.beats}</span>
                {row.target_note && <span className="ml-1 text-emerald-300">♪{row.target_note}</span>}
              </span>
            ))}
            {result.chord_timeline.length > 64 && (
              <span className="text-xs text-stone-500 self-center">+{result.chord_timeline.length - 64}</span>
            )}
          </div>
        </div>
      )}

      <div className="text-[11px] text-stone-500 italic">
        Confiance moyenne : {(result.confidence_avg * 100).toFixed(0)}%. Tu pourras ajuster manuellement chaque accord après application.
      </div>

      <div className="flex justify-end gap-2">
        <button onClick={onRestart} className="btn-ghost text-sm">Recommencer</button>
        <button onClick={onApply} data-testid="studio-analyze-apply" className="btn-primary text-sm">
          <CheckCircle2 size={13} /> Appliquer à la timeline
        </button>
      </div>
    </div>
  );
}

// ---------- HISTORY PANEL ----------
function HistoryPanel({ history, onReuse }) {
  if (history.length === 0) {
    return (
      <div className="rounded-lg border border-stone-800 bg-stone-900/60 p-8 text-center">
        <Music size={22} className="mx-auto text-stone-600 mb-2" />
        <div className="text-sm text-stone-400">Aucune analyse précédente</div>
        <div className="text-xs text-stone-600 mt-1">Tes analyses réussies s'afficheront ici pour une ré-application sans consommer ton quota.</div>
      </div>
    );
  }
  return (
    <div className="space-y-2" data-testid="analyze-history-list">
      {history.map((h) => {
        const dt = h.completed_at ? new Date(h.completed_at).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' }) : '';
        return (
          <div key={h.job_id} className="rounded-lg border border-stone-800 bg-stone-900/60 p-3 flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-orange-950/40 border border-orange-900/60 flex items-center justify-center shrink-0">
              <Music size={14} className="text-orange-400" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm text-stone-100">
                {h.bpm} BPM · {h.chord_count} mesures · {h.distinct_chords} accords
                {h.sections_count > 0 && <span className="text-orange-300"> · {h.sections_count} sections</span>}
              </div>
              <div className="text-[11px] text-stone-500 truncate">
                {dt}{h.duration_sec ? ` · ${Math.round(h.duration_sec)}s` : ''} · conf {Math.round((h.confidence_avg || 0) * 100)}%
              </div>
            </div>
            <button
              onClick={() => onReuse(h.job_id)}
              data-testid={`analyze-reuse-${h.job_id}`}
              className="text-xs px-2.5 py-1 rounded-full bg-emerald-700 hover:bg-emerald-600 text-white font-semibold shrink-0"
              title="Ré-appliquer cette analyse sans consommer de quota"
            >
              Réutiliser
            </button>
          </div>
        );
      })}
    </div>
  );
}

function StatMini({ label, value }) {
  return (
    <div className="rounded-lg bg-stone-900/70 border border-stone-800 p-2">
      <div className="text-[10px] uppercase tracking-wider text-stone-500">{label}</div>
      <div className="font-display text-2xl text-orange-300">{value}</div>
    </div>
  );
}
